<script lang="ts">
	import { onMount } from 'svelte';
	import type { ApexOptions } from 'apexcharts';
	import Chart from '../Chart.svelte';
	import { getAlertSeries } from '../../AppNetwork';
	import type { AlertSeries } from '../../AppTypes';

	export let alertId: number;

	let series: AlertSeries | null = null;
	let loading = true;
	let error = '';

	onMount(async () => {
		try {
			series = await getAlertSeries(alertId);
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to load chart';
		} finally {
			loading = false;
		}
	});

	const round = (v: number) => Math.round(v * 100) / 100;

	$: latest = series && series.points.length > 0 ? series.points[series.points.length - 1] : null;
	$: hasBand = !!(latest && latest.ema !== null);
	$: latestValue = latest ? round(latest.value) : 0;
	$: latestLower = latest && latest.lower !== null ? round(latest.lower) : 0;
	$: latestUpper = latest && latest.upper !== null ? round(latest.upper) : 0;
	$: anomalous = !!(series && latest && latest.ema !== null && (
		(series.direction !== 'below' && latest.upper !== null && latest.value > latest.upper) ||
		(series.direction !== 'above' && latest.lower !== null && latest.value < latest.lower)
	));

	function buildOptions(s: AlertSeries): ApexOptions {
		const at = (key: 'value' | 'ema' | 'upper' | 'lower') =>
			s.points.map((p) => ({ x: new Date(p.t).getTime(), y: p[key] }));

		// Only show the band edge(s) the alert actually reacts to.
		const series = [
			{ name: 'Value', data: at('value') },
			{ name: 'EMA', data: at('ema') }
		];
		if (s.direction !== 'below') series.push({ name: `+${s.sigma}σ`, data: at('upper') });
		if (s.direction !== 'above') series.push({ name: `−${s.sigma}σ`, data: at('lower') });

		const bandCount = series.length - 2;
		return {
		chart: {
			type: 'line',
			height: 240,
			fontFamily: 'inherit',
			foreColor: '#94a3b8',
			toolbar: { show: false },
			zoom: { enabled: false },
			animations: { enabled: false }
		},
		series,
		colors: ['#3b82f6', '#6366f1', ...Array(bandCount).fill('#f59e0b')],
		dataLabels: { enabled: false },
		stroke: { curve: 'smooth', width: [2, 2, ...Array(bandCount).fill(1)], dashArray: [0, 5, ...Array(bandCount).fill(3)] },
		legend: { show: true, position: 'top', horizontalAlign: 'right' },
		grid: { borderColor: 'rgba(148,163,184,0.2)', strokeDashArray: 4 },
		markers: { size: 0, hover: { size: 4 } },
		xaxis: { type: 'datetime', labels: { datetimeUTC: false }, axisBorder: { show: false }, axisTicks: { show: false } },
		yaxis: { labels: { formatter: (v: number) => `${round(v)}` } },
		tooltip: { theme: 'dark', x: { format: 'MMM dd, HH:mm' }, y: { formatter: (v: number) => (v === null ? 'n/a' : `${round(v)}`) } }
	} as ApexOptions;
	}
</script>

<div class="p-4">
	{#if loading}
		<p class="text-sm text-gray-400 py-6 text-center"><i class="fa-solid fa-spinner fa-spin mr-2"></i>Loading chart…</p>
	{:else if error}
		<p class="text-sm text-red-500 py-6 text-center">{error}</p>
	{:else if !series || series.points.length === 0}
		<p class="text-sm text-gray-400 py-6 text-center">No samples for this metric in the EMA window yet.</p>
	{:else}
		<div class="flex items-center justify-between mb-2">
			<span class="text-sm font-medium text-gray-900 dark:text-white">{series.metricKey}</span>
			{#if latest}
				<span class="text-xs {anomalous ? 'text-amber-500 font-semibold' : 'text-gray-400'}">
					current {latestValue}{#if hasBand} · band {latestLower} … {latestUpper}{/if}
					{#if anomalous} · anomalous{/if}
				</span>
			{/if}
		</div>
		<Chart options={buildOptions(series)} />
		<p class="text-xs text-gray-400 mt-2">Value vs its exponential moving average and ±{series.sigma}σ band over the alert's window.</p>
	{/if}
</div>
